export enum ApiResponseStatus {
  SUCCESS = 200,
  CREATED = 201,
  ALREADY_EXIST = 302,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  METHOD_NOT_ALLOWED = 405,
  SERVER_ERROR = 500,
}

export enum AppMode {
  START = 'start',
  LOCAL = 'local',
  STAGE = 'stage',
  PROD = 'prod',
}

export enum AuthStatus {
  NOT_STARTED = 'not started',
  PENDING = 'pending',
  PASSED = 'passed',
}

export enum CookieName {
  GA = '_ga',
}

export enum ErrorPhrase {
  TEXT_GENERATOR_NO_REQUEST = 'validation_error.text_generator_no_request',
  FILES_UPLOAD_COUNT_EXCEEDED = 'validation_error.files_upload_count_exceeded',
  FILES_UPLOAD_SIZE_EXCEEDED = 'validation_error.files_upload_size_exceeded',
  FILES_UPLOAD_ERROR = 'validation_error.files_upload_error',
  UNSUPPORTED_FILE_TYPE = 'validation_error.unsupported_file_type',
  CLIPBOARD_PERMISSION_DENIED = 'validation_error.clipboard_permission_denied',
  EDITOR_LONG_USER_REQUEST = 'validation_error.editor_long_user_request',
  EDITOR_SHORT_USER_REQUEST = 'validation_error.editor_short_user_request',
  FUNNEL_LONG_USER_REQUEST = 'validation_error.funnel_long_user_request',
  FUNNEL_SHORT_USER_REQUEST = 'validation_error.funnel_short_user_request',
  PLAGIARISM_CHECKER_TEXT_TOO_SHORT = 'validation_error.plagiarism_checker_text_too_short',
  PLAGIARISM_CHECKER_TEXT_TOO_LONG = 'validation_error.plagiarism_checker_text_too_long',
  NO_USER_ID = 'validation_error.no_user_id',
  NO_TEXT_ID = 'validation_error.no_text_id',
  BASE_ERROR = 'validation_error.base_error',
  SERVICE_UNAVAILABLE = 'validation_error.service_unavailable',
  REQUEST_FAILED = 'validation_error.request_failed',
  REPORT_GENERATION_FAILED = 'validation_error.report_generation_failed',
}

export enum FetchStatus {
  IDLE = 'idle',
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

export enum GeneratingStatus {
  IDLE = 'idle',
  IN_PROGRESS = 'in-progress',
  SUCCESS = 'success',
  ERROR = 'error',
}

export enum GenerationType {
  HUMANIZE = 'humanize',
  HUMANIZE_V2 = 'humanize_v2',
  AI_DETECTOR = 'ai_detector',
  AI_DETECTOR_AFTER_HUMANIZE = 'ai_detector_after_humanize',
  SOLUTION = 'solution',
  REWRITE = 'rewrite',
  ESSAY_WRITER = 'essay_writer',
  AI_WRITER = 'ai_writer',
  PLAG_CHECK = 'plag_check',
}

export enum LengthInChars {
  EDITOR_USER_REQUEST_MIN = 5,
  EDITOR_USER_REQUEST_MAX = 5000,
  FUNNEL_USER_REQUEST_MIN = 10,
  FUNNEL_USER_REQUEST_MAX = 50000,
}

export enum Locale {
  ENGLISH = 'en-US',
  FRENCH = 'fr-FR',
  GERMAN = 'de-DE',
  SPANISH = 'es-ES',
  ITALIAN = 'it-IT',
  PORTUGUESE = 'pt-BR',
}

export enum NodeEnv {
  DEVELOPMENT = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production',
}

export enum SaveStatus {
  IDLE = 'idle',
  UNSAVED = 'unsaved',
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
}

export enum SearchParam {
  NEW_REQUEST = 'new_request',
  INCOMING_REQUEST = 'incoming_request',
  SAVED_TEXT = 'saved_text',
  IS_FUNNEL_TOOL = 'is_funnel_tool',
  AUTO_SUBMIT = 'auto_submit',
  IS_AUTO_TEST = 'is_autotest',
  JUST_COME_AFTER_PAY = 'just_come_after_pay',
  SKIP_TRY_PAY_BY_TOKEN = 'skip_try_pay_by_token',
  OPEN_PAY_AS_YOU_GO_POPUP = 'open_popup_pay_as_you_go',
  VARIANT = 'variant',
  AUTO_OPEN_PAYWALL = 'autoopen_prices_popup_ai_gen',
  AUTO_OPEN_TRIAL_MONTHLY_REBILL = 'autoopen_trial_monthly_rebill_popup_ai_gen',
  /** The popup a link opens, one of `MODAL_SEARCH_PARAM_VALUE`. */
  MODAL = 'modal',
  /** Forces the purchase/upgrade mode of the plans request. */
  OPERATION_TYPE = 'operation_type_bp',
  /** Pre-selects a plan by id. */
  SELECT_PACKAGE = 'select_package',
  REPORT_NO_VISIBILITY_GATES = 'report_no_gates',
  /** Logs what the report's measure pass read off the DOM. */
  REPORT_DEBUG_MEASURE = 'report_debug',
}

export const MODAL_SEARCH_PARAM_VALUE = {
  RESCUE: 'rescue',
  UPGRADE_ANNUAL: 'upgrade_annual',
} as const;

export enum StorageType {
  SESSION = 'sessionStorage',
  LOCAL = 'localStorage',
}

export const VIEWPORT_BREAKPOINT_PX = {
  SM: 768,
  MD: 1024,
} as const;

export const AI_INPUT_TAGS = [
  'Longer',
  'Shorter',
  'Simplify',
  'Explain',
  'Paraphrase',
  'Add examples',
] as const;

export const SAMPLE_TEXT = {
  DEFAULT: 'ai.funnel_detector_sample_text',
  PLAGIARISM_CHECKER: 'ai.funnel_plagiarism_checker_sample_text',
} as const;

export const IconSize = {
  TEXT_CONTROL: {
    width: 24,
    height: 24,
  },
  LOADER_SIZE_XXS: {
    width: 12,
    height: 12,
  },
  LOADER_SIZE_XS: {
    width: 18,
    height: 18,
  },
  LOADER_SIZE_S: {
    width: 24,
    height: 24,
  },
  LOADER_SIZE_M: {
    width: 36,
    height: 36,
  },
  LOADER_SIZE_L: {
    width: 48,
    height: 48,
  },
  NEW_TEXT: {
    width: 32,
    height: 32,
  },
};

// Word Counter: fixed number of words that make up a single page
export const WORDS_PER_PAGE = 275;

/** Decimal places of a score shown as a percentage (AI share, flagged share). */
export const SCORE_FRACTION_DIGITS = 1;

export const STORAGE_KEYS = {
  AI_HISTORY_BADGE: 'ai_history_badge',
  PLAGIARISM_CHECKER_INITIAL_TEXT: 'plagiarismCheckerInitialText',
  PLAGIARISM_CHECKER_TEXT_PERCENTAGE: 'plagiarismCheckerTextPercentage',
  PLAGIARISM_CHECKER_SAVED_TEXT: 'plagiarismCheckerSavedText',
  AI_DETECTOR_INITIAL_TEXT: 'aiDetectorInitialText',
  AI_DETECTOR_INITIAL_DETECTOR_TO_BYPASS: 'aiDetectorInitialDetectorToBypass',
  AI_DETECTOR_SAVED_TEXT: 'aiDetectorSavedText',
  AI_DETECTOR_AUTO_SUBMIT: 'aiDetectorAutoSubmit',
  AI_HUMANIZER_INITIAL_TEXT: 'aiHumanizerInitialText',
  AI_HUMANIZER_INITIAL_DETECTOR_TO_BYPASS: 'aiHumanizerInitialDetectorToBypass',
  AI_HUMANIZER_INITIAL_USER_REQUEST_AI_SCORE:
    'aiHumanizerInitialUserRequestAiScore',
  AI_HUMANIZER_INITIAL_HUMANIZED_TEXT_AI_SCORE:
    'aiHumanizerInitialHumanizedTextAiScore',
  AI_HUMANIZER_AUTO_SUBMIT: 'aiHumanizerAutoSubmit',
  AI_HUMANIZER_SKIP_DEBIT: 'aiHumanizerSkipDebit',
  AI_HUMANIZER_SAVED_TEXT: 'aiHumanizerSavedText',
  PARAPHRASE_INITIAL_TEXT: 'paraphraseInitialText',
  FACT_CHECKER_INITIAL_TEXT: 'factCheckerInitialText',
  GRAMMAR_CHECKER_INITIAL_TEXT: 'grammarCheckerInitialText',
  REAL_EXPERT_HUMANIZE_INITIAL_TEXT: 'realExpertHumanizeInitialText',
  AI_HUMANIZER_SETTINGS: 'aiHumanizerSettings',
  ONBOARDING_TOOLTIPS_SHOWED: 'onboardingTooltipsShowed',
  IS_AUTO_TEST: 'isAutoTest',
  QUIZ_ANSWERS: 'quizAnswers',
  // START ES38606
  // MARKER FOR PREVENTING OLD USERS WHO HAS ALREADY COMPLETED THE QUIZ FROM THE OLD FLOW TO ACCESS THE NEW FLOW
  QUIZ_ANSWERS_ES38606_MARKER: 'quizAnswersEs38606',
  // END ES38606
  AI_DETECTOR_ES37975_FIRST_CHECK: 'aiDetectorEs37975FirstCheck',
  SUBSCRIPTION_POPUP_TIMER: 'ai_tools_timer_seconds',
  /** Dev overrides of the Replit tool domains, `{ [replitToolId]: domain }`. */
  REPLIT_DOMAIN_OVERRIDES: 'replitDomainOverrides',
};

export enum LoginEventPopupFlow {
  FUNNEL = 'funnel',
  PRICING = 'pricing',
}

export const ELEMENT_IDS = {
  DETECTOR_LAYOUT: 'js_detector_layout',
  // Not the host `js_update_pm_notice`: its script still loads and would bind to it.
  PM_NOTICE: 'update-pm-notice',
  AI_DETECTOR_INITIAL_TEXT: 'js_data_user_ai_detector_initial_text',
  AI_HUMANIZER_INITIAL_TEXT: 'js_data_user_ai_humanizer_initial_text',
  PLAGIARISM_CHECKER_INITIAL_TEXT:
    'js_data_user_plagiarism_checker_initial_text',
  SUBSCRIPTION_POPUP_PLANS: 'js_subscription_popup_plans',
};

/** Mount node of the SPA, rendered by the host page. */
export const ROOT_ID = 'root';

export const PDF_DOCUMENT_ID = 'pdf-document';

/** A4 at 96 dpi. 210mm = 793.7px and 297mm = 1122.5px; rounded for crisp layout. */
export const PDF_PAGE_WIDTH_PX = 794;
export const PDF_PAGE_HEIGHT_PX = 1123;

/** Marks one capturable page inside the PDF root; the value is the page kind. */
export const PDF_PAGE_ATTR = 'data-pdf-page';
/**
 * Marks a page rendered only to be measured. Deliberately a different attribute:
 * `PDF_PAGE_ATTR` is the capture contract, so a page that is not ready to be captured must
 * not carry it — a capture that starts too early then fails loudly instead of quietly
 * producing a PDF of the measuring layout.
 */
export const PDF_MEASURE_PAGE_ATTR = 'data-pdf-measure-page';
/** Marks a clickable area; the value is the destination URL. */
export const PDF_LINK_ATTR = 'data-pdf-link';

/** Upper bound on waiting for the report to finish paginating before capturing. */
export const REPORT_READY_TIMEOUT_MS = 8000;
/** Upper bound on the AI-history lookup for the report's document title and date. */
export const REPORT_DOCUMENT_TIMEOUT_MS = 10000;
/** Upper bound on rasterizing the pages into the PDF. */
export const REPORT_CAPTURE_TIMEOUT_MS = 180000;
/** Upper bound on waiting for web fonts; a slow face costs a re-wrap, not the download. */
export const REPORT_FONTS_TIMEOUT_MS = 5000;
/** Limit for the request that attaches the rescue discount to the user. */
export const RESCUE_DISCOUNT_TIMEOUT_MS = 15000;
/** Limit for a userData refresh: after a login, before a words tool run. */
export const USER_DATA_REFRESH_TIMEOUT_MS = 15000;
/** Upper bound on loading a Replit tool bundle before the page shows an error. */
export const REPLIT_BUNDLE_TIMEOUT_MS = 30000;
